/**
 * AdminUsersService - Serviço para gerenciar usuários no painel admin
 * Apenas usuários admin têm acesso a estas APIs
 */
import { api } from '../lib/api';

export const adminUsersService = {
  // ============================================
  // LISTAGEM E BUSCA
  // ============================================

  /**
   * Listar todos os usuários com filtros e paginação
   */
  async listUsers(params = {}) {
    const {
      limit = 50,
      offset = 0,
      plan = null,
      status = null,
      search = null,
      is_admin = null,
      sort_by = 'created_at',
      sort_order = 'desc'
    } = params;

    const queryParams = new URLSearchParams();
    queryParams.append('limit', limit);
    queryParams.append('offset', offset);
    queryParams.append('sort_by', sort_by);
    queryParams.append('sort_order', sort_order);

    if (plan) queryParams.append('plan', plan);
    if (status) queryParams.append('status', status);
    if (search) queryParams.append('search', search);
    if (is_admin !== null) queryParams.append('is_admin', is_admin);

    const response = await api.get(`/api/admin/users?${queryParams.toString()}`);
    return response.data;
  },

  /**
   * Obter detalhes de um usuário específico
   */
  async getUserDetails(userId) {
    const response = await api.get(`/api/admin/users/${userId}`);
    return response.data;
  },

  /**
   * Obter estatísticas gerais de usuários
   */
  async getStatistics() {
    const response = await api.get('/api/admin/users/stats/overview');
    return response.data;
  },

  /**
   * Obter analytics detalhados para dashboard
   */
  async getAnalytics() {
    const response = await api.get('/api/admin/users/stats/analytics');
    return response.data;
  },

  // ============================================
  // AÇÕES DE GERENCIAMENTO
  // ============================================

  /**
   * Adicionar créditos extras ao usuário
   */
  async addCredits(userId, credits) {
    const response = await api.patch(`/api/admin/users/${userId}/credits`, {
      images: credits.images || 0,
      videos: credits.videos || 0,
      reason: credits.reason || null
    });
    return response.data;
  },

  /**
   * Atualizar assinatura do usuário
   */
  async updateSubscription(userId, data) {
    const response = await api.patch(`/api/admin/users/${userId}/subscription`, {
      plan: data.plan || null,
      status: data.status || null,
      extend_days: data.extend_days || null,
      reset_usage: data.reset_usage || false
    });
    return response.data;
  },

  /**
   * Resetar contadores de uso
   */
  async resetUsage(userId) {
    const response = await api.patch(`/api/admin/users/${userId}/reset-usage`);
    return response.data;
  },

  /**
   * Excluir usuário permanentemente
   */
  async deleteUser(userId, confirmEmail) {
    const response = await api.delete(`/api/admin/users/${userId}?confirm_email=${encodeURIComponent(confirmEmail)}`);
    return response.data;
  },

  /**
   * Promover ou remover status de admin
   */
  async toggleAdmin(userId, isAdmin) {
    const response = await api.patch(`/api/admin/users/${userId}/toggle-admin`, {
      is_admin: isAdmin
    });
    return response.data;
  },

  // ============================================
  // HELPERS DE FORMATAÇÃO
  // ============================================

  helpers: {
    /**
     * Formatar status da subscription para exibição
     */
    formatSubscriptionStatus(status, plan = null) {
      // 'inactive' + plano free = usuário do plano GRÁTIS (nunca assinou),
      // e não uma conta desativada — mostrar "Sem assinatura" evita a leitura
      // errada de que o usuário está bloqueado.
      if (status === 'inactive' && (!plan || plan === 'free')) {
        return { text: 'Sem assinatura', color: 'gray', emoji: '🆓' };
      }
      const statusMap = {
        active: { text: 'Ativo', color: 'green', emoji: '✅' },
        trialing: { text: 'Trial', color: 'blue', emoji: '🔵' },
        canceled: { text: 'Cancelado', color: 'red', emoji: '❌' },
        inactive: { text: 'Inativo', color: 'gray', emoji: '⚪' },
        past_due: { text: 'Pagamento Pendente', color: 'orange', emoji: '⚠️' }
      };
      return statusMap[status] || { text: status || 'Desconhecido', color: 'gray', emoji: '❓' };
    },

    /**
     * Formatar plano para exibição
     */
    formatPlan(plan, billingInterval = null) {
      const planMap = {
        free: { text: 'Free', color: 'gray', emoji: '🆓' },
        basic: { text: 'Basic', color: 'blue', emoji: '⭐' },
        pro: { text: 'Pro', color: 'purple', emoji: '🚀' },
        max: { text: 'Max', color: 'amber', emoji: '👑' }
      };
      const info = planMap[plan] || { text: plan || 'Free', color: 'gray', emoji: '🆓' };
      
      // Adicionar sufixo do intervalo se fornecido
      if (billingInterval && plan !== 'free') {
        info.text += billingInterval === 'yearly' ? ' (Anual)' : ' (Mensal)';
      }
      
      return info;
    },

    /**
     * Formatar data para exibição
     */
    formatDate(dateString) {
      if (!dateString) return 'Nunca';
      
      try {
        const date = new Date(dateString);
        return new Intl.DateTimeFormat('pt-BR', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit'
        }).format(date);
      } catch {
        return dateString;
      }
    },

    /**
     * Formatar data relativa (há X dias)
     */
    formatRelativeDate(dateString) {
      if (!dateString) return 'Nunca';
      
      try {
        const date = new Date(dateString);
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffMinutes = Math.floor(diffMs / (1000 * 60));

        if (diffMinutes < 1) return 'Agora';
        if (diffMinutes < 60) return `Há ${diffMinutes}min`;
        if (diffHours < 24) return `Há ${diffHours}h`;
        if (diffDays === 1) return 'Ontem';
        if (diffDays < 7) return `Há ${diffDays} dias`;
        if (diffDays < 30) return `Há ${Math.floor(diffDays / 7)} semanas`;
        if (diffDays < 365) return `Há ${Math.floor(diffDays / 30)} meses`;
        return `Há ${Math.floor(diffDays / 365)} anos`;
      } catch {
        return dateString;
      }
    },

    /**
     * Calcular porcentagem de uso
     */
    calculateUsagePercentage(used, limit) {
      if (!limit || limit === 0) return 0;
      return Math.min(100, Math.round((used / limit) * 100));
    },

    /**
     * Obter cor baseada na porcentagem de uso
     */
    getUsageColor(percentage) {
      if (percentage >= 90) return 'red';
      if (percentage >= 70) return 'orange';
      if (percentage >= 50) return 'yellow';
      return 'green';
    },

    /**
     * Formatar número com separador de milhares
     */
    formatNumber(num) {
      if (num === null || num === undefined) return '0';
      return new Intl.NumberFormat('pt-BR').format(num);
    },

    /**
     * Obter iniciais do nome
     */
    getInitials(name) {
      if (!name) return '?';
      const parts = name.trim().split(' ');
      if (parts.length === 1) return parts[0].charAt(0).toUpperCase();
      return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
    },

    /**
     * Gerar cor de avatar baseada no nome
     */
    getAvatarColor(name) {
      const colors = [
        'from-red-400 to-red-600',
        'from-orange-400 to-orange-600',
        'from-amber-400 to-amber-600',
        'from-yellow-400 to-yellow-600',
        'from-lime-400 to-lime-600',
        'from-green-400 to-green-600',
        'from-emerald-400 to-emerald-600',
        'from-teal-400 to-teal-600',
        'from-cyan-400 to-cyan-600',
        'from-sky-400 to-sky-600',
        'from-blue-400 to-blue-600',
        'from-indigo-400 to-indigo-600',
        'from-violet-400 to-violet-600',
        'from-purple-400 to-purple-600',
        'from-fuchsia-400 to-fuchsia-600',
        'from-pink-400 to-pink-600',
        'from-rose-400 to-rose-600'
      ];
      
      if (!name) return colors[0];
      
      let hash = 0;
      for (let i = 0; i < name.length; i++) {
        hash = name.charCodeAt(i) + ((hash << 5) - hash);
      }
      
      return colors[Math.abs(hash) % colors.length];
    },

    /**
     * Verificar se período está expirando em breve (7 dias)
     */
    isExpiringSoon(endDateString) {
      if (!endDateString) return false;
      
      try {
        const endDate = new Date(endDateString);
        const now = new Date();
        const diffDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
        return diffDays > 0 && diffDays <= 7;
      } catch {
        return false;
      }
    },

    /**
     * Verificar se período já expirou
     */
    isExpired(endDateString) {
      if (!endDateString) return false;
      
      try {
        const endDate = new Date(endDateString);
        return new Date() > endDate;
      } catch {
        return false;
      }
    },

    /**
     * Dias restantes no período
     */
    daysRemaining(endDateString) {
      if (!endDateString) return null;
      
      try {
        const endDate = new Date(endDateString);
        const now = new Date();
        const diffDays = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
        return diffDays;
      } catch {
        return null;
      }
    }
  }
};

export default adminUsersService;